import React, { useState, useEffect, useRef } from 'react';
import PlayerService from '../../services/PlayerService';
import NotificationManager from '../../utils/NotificationManager';
import * as d3 from 'd3';
import './ReferralNetworkModule.css';

const ReferralNetworkModule = ({ onBack, linkedProfile }) => {
  const [networkData, setNetworkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maxDepth, setMaxDepth] = useState(0);
  const [highlightedUserId, setHighlightedUserId] = useState(linkedProfile?.playerId);
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const [allNodes, setAllNodes] = useState([]);
  
  // Refs for D3 visualization
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // Get rank color based on rank
  const getRankColor = (rank) => {
    const rankColors = {
      '0': "#A9A9A9", // Light Gray
      '1': "#00BFFF", // Deep Sky Blue
      '2': "#9370DB", // Medium Purple
      '3': "#FFD700", // Gold
      '4': "#008080", // Teal
      '5': "#8A2BE2", // Blue Violet
      '6': "#FD6907"  // Dark Orange
    };
    return rankColors[rank] || "#33ff33";
  };
  
  // Get rank name
  const getRankName = (rank) => {
    const rankNames = {
      '0': "Signal Seeker",
      '1': "Void Aligned",
      '2': "Sentient",
      '3': "Frequency Specialist",
      '4': "Echo Bound",
      '5': "Resonance Manipulator",
      '6': "VOID"
    };
    return rankNames[rank] || "Unknown";
  };

  useEffect(() => {
    fetchCompleteReferralNetwork();
  }, []);

  const fetchCompleteReferralNetwork = async () => {
    try {
      setLoading(true);
      
      // Get all players from Firebase
      const allPlayers = await PlayerService.getAllPlayers();
      if (!allPlayers || allPlayers.length === 0) {
        setError('No player data found');
        setLoading(false);
        return;
      }

      console.log(`Fetched ${allPlayers.length} players for network map`);
      
      // Create a map of all players for quick lookup
      const playerMap = {};
      allPlayers.forEach(player => {
        playerMap[player.id] = player;
      });
      
      // Find root nodes (players who weren't referred by anyone)
      const rootNodes = allPlayers.filter(player => !player.referredBy);
      
      // Build the complete network
      const completeNetwork = {
        id: 'root',
        username: 'Complete Network',
        qubits: 0,
        depth: 0,
        children: []
      };
      
      // Process each root node
      for (const rootPlayer of rootNodes) {
        const rootNode = await buildNetworkNode(rootPlayer.id, rootPlayer, playerMap, 1);
        completeNetwork.children.push(rootNode);
      }
      
      setNetworkData(completeNetwork);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching complete referral network:', error);
      setError('Failed to load referral network');
      setLoading(false);
    }
  };
  
  // Build a node in the network
  const buildNetworkNode = async (playerId, playerData, playerMap, depth) => {
    // Update max depth for visualization
    if (depth > maxDepth) {
      setMaxDepth(depth);
    }

    // Base node with user data
    const node = {
      id: playerId,
      username: playerData.discordUsername || 'Unknown User',
      qubits: playerData.qubits || 0,
      rank: playerData.rank || '0',
      depth: depth,
      children: []
    };
    
    // Find all players who were referred by this player
    const referredPlayers = Object.values(playerMap).filter(
      player => player.referredBy === playerId
    );
    
    // Process each referred player (with depth limit to prevent excessive nesting)
    if (referredPlayers.length > 0 && depth < 5) {
      for (const referredPlayer of referredPlayers) {
        const childNode = await buildNetworkNode(
          referredPlayer.id, 
          referredPlayer, 
          playerMap, 
          depth + 1
        );
        node.children.push(childNode);
      }
    }
    
    return node;
  };

  // Store the current transform to maintain view position
  const currentTransformRef = useRef(null);
  
  // Calculate stroke width based on qubits
  const calculateStrokeWidth = (qubits, isHighlighted) => {
    // More qubits = thicker stroke, with a minimum for highlighted nodes
    return isHighlighted ? Math.max(3, 1 + Math.log10(qubits + 1)) : Math.max(1, Math.log10(qubits + 1));
  };
  
  // Calculate glow intensity based on qubits
  const calculateGlowIntensity = (qubits) => {
    // More qubits = stronger glow
    return Math.min(5, 1 + Math.log10(qubits + 1) * 0.8);
  };
  
  // Initialize D3 visualization when data is loaded
  useEffect(() => {
    if (!networkData || !svgRef.current) return;
    
    // Clear any existing visualization
    d3.select(svgRef.current).selectAll("*").remove();
    
    // Create the SVG container with zoom functionality
    const svg = d3.select(svgRef.current);
    const width = parseInt(svg.style("width")) || 800;
    const height = parseInt(svg.style("height")) || 600;
    
    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });
    
    svg.call(zoom)
      .on("zoom", (event) => {
        // Store the current transform whenever it changes
        currentTransformRef.current = event.transform;
      });
    
    // Create a group for the graph
    const g = svg.append("g")
      .attr("class", "network-graph");
    
    // Add a background rect to catch zoom events
    g.append("rect")
      .attr("width", width * 2)
      .attr("height", height * 2)
      .attr("x", -width / 2)
      .attr("y", -height / 2)
      .attr("fill", "none")
      .attr("pointer-events", "all");
    
    // Create defs for glow filters with different intensities
    const defs = svg.append("defs");
    
    // Create improved glow filters with different intensities
    for (let i = 1; i <= 5; i++) {
      const filter = defs.append("filter")
        .attr("id", `glow-${i}`)
        .attr("x", "-50%")
        .attr("y", "-50%")
        .attr("width", "200%")
        .attr("height", "200%"); // Expanded filter region to prevent clipping
      
      // Add a slight offset to prevent pixelation
      filter.append("feOffset")
        .attr("dx", "0")
        .attr("dy", "0")
        .attr("result", "offOut");
      
      // Improved blur with higher quality
      filter.append("feGaussianBlur")
        .attr("in", "offOut")
        .attr("stdDeviation", i * 1.5) // Increasing blur based on intensity
        .attr("result", "blurOut");
      
      // Smoother color matrix
      filter.append("feColorMatrix")
        .attr("in", "blurOut")
        .attr("type", "matrix")
        .attr("values", "0 0 0 0 0 0 1 0 0 1 0 0 0 0 0 0 0 0 0.7 0")
        .attr("result", "coloredBlur");
      
      // Composite the original and the glow
      const feMerge = filter.append("feMerge");
      feMerge.append("feMergeNode").attr("in", "coloredBlur");
      feMerge.append("feMergeNode").attr("in", "SourceGraphic");
    }
    
    // Calculate node size based on qubits and rank - more emphasis on qubits
    // Smaller nodes for inner circles (higher ranks)
    const calculateNodeSize = (qubits, rank) => {
      // Handle extremely large qubit values (336,000+) with a more controlled scaling
      // Base size is smaller for higher ranks (center)
      const rankFactor = 1 - (parseInt(rank) / 10); // Rank 6: 0.4, Rank 0: 1.0
      const baseSize = 25 * rankFactor; // Base size for nodes with few qubits
      const minSize = 15 + (6 - parseInt(rank)) * 2; // Minimum size
      
      // Use a logarithmic scale with a cap for extremely large values
      const logValue = Math.log10(qubits + 10);
      const qubitSize = Math.min(60, Math.pow(logValue, 1.5) * 5);
      
      // Add a maximum size to prevent nodes from getting too large
      const maxSize = 100;
      
      return Math.min(maxSize, Math.max(minSize, baseSize + qubitSize));
    };
    
    // These functions are now defined outside the useEffect
    
    // Create a radial layout based on ranks
    // First, extract all nodes into a flat array
    const extractedNodes = [];
    const extractAllNodes = (node) => {
      if (!node) return;
      
      // Skip the root node
      if (node.id !== 'root') {
        extractedNodes.push(node);
      }
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          extractAllNodes(child);
        });
      }
    };
    
    extractAllNodes(networkData);
    
    // Update state with all nodes
    setAllNodes(extractedNodes);
    
    console.log(`Extracted ${extractedNodes.length} nodes for radial visualization`);
    
    // Group nodes by rank
    const nodesByRank = {};
    for (let rank = 0; rank <= 6; rank++) {
      nodesByRank[rank] = extractedNodes.filter(node => parseInt(node.rank || '0') === rank);
    }
    
    // Sort nodes within each rank by qubits (descending)
    Object.values(nodesByRank).forEach(rankGroup => {
      rankGroup.sort((a, b) => b.qubits - a.qubits);
    });
    
    // Create a map of referral relationships
    const referralMap = {};
    extractedNodes.forEach(node => {
      if (node.referredBy) {
        referralMap[node.id] = node.referredBy;
      }
    });
    
    // Calculate positions for each node in a radial layout
    const radialNodes = [];
    const radialLinks = [];
    
    // Center of the visualization
    const centerX = width / 2;
    const centerY = height / 2;

    // Seed of Life sacred geometry layout
    // One flower — all 7 circles overlap at center. Ranks radiate outward
    // from the core: R6 innermost → R0 outermost, through the petals.
    // Flower of Life layout — proper hexagonal lattice.
    // Every node IS a circle of radius nodeR. Centers sit on a triangular
    // grid with spacing nodeR, so adjacent circles always overlap correctly.
    const nodeR = 250;
    const R = nodeR;

    // Generate hex grid positions using axial coordinates (q, r).
    // Ring 0 = 1 center, Ring k = 6k positions. Walk the hex ring perimeter.
    const hexDirs = [[-1,1],[-1,0],[0,-1],[1,-1],[1,0],[0,1]];
    const hexRings = [];

    // Ring 0
    hexRings.push([{ q: 0, r: 0 }]);

    // Rings 1–12 (plenty of capacity)
    for (let ring = 1; ring <= 12; ring++) {
      const positions = [];
      let q = ring, r = 0; // start at "east" corner
      for (let side = 0; side < 6; side++) {
        for (let step = 0; step < ring; step++) {
          positions.push({ q, r });
          q += hexDirs[side][0];
          r += hexDirs[side][1];
        }
      }
      hexRings.push(positions);
    }

    // Axial → pixel conversion (pointy-top, spacing = nodeR)
    const hexToPixel = (q, r) => ({
      x: centerX + nodeR * (q + r * 0.5),
      y: centerY + nodeR * (r * Math.sqrt(3) * 0.5),
    });

    // Flatten all hex positions into one list, center outward
    // Skip ring 0 (center) — that's reserved for the VOID marker
    const allHexPositions = [];
    hexRings.forEach((ring, ringIdx) => {
      if (ringIdx === 0) return;
      ring.forEach(pos => {
        allHexPositions.push({ ...pos, ring: ringIdx });
      });
    });

    // Sort all nodes by rank (R6 first → R0 last), then by qubits
    const sortedNodes = [];
    for (let rank = 6; rank >= 0; rank--) {
      const nodesInRank = (nodesByRank[rank] || []).slice();
      nodesInRank.sort((a, b) => b.qubits - a.qubits);
      sortedNodes.push(...nodesInRank);
    }

    // Place each node on the next hex position — one continuous flower
    sortedNodes.forEach((node, index) => {
      if (index >= allHexPositions.length) return;
      const pos = allHexPositions[index];
      const px = hexToPixel(pos.q, pos.r);
      radialNodes.push({
        ...node,
        x: px.x,
        y: px.y,
        angle: Math.atan2(px.y - centerY, px.x - centerX),
        radius: pos.ring * nodeR,
      });
    });
    
    // Create links only for actual referral relationships
    radialNodes.forEach(node => {
      if (node.referredBy) {
        // Find the referrer node
        const referrer = radialNodes.find(n => n.id === node.referredBy);
        if (referrer) {
          radialLinks.push({
            source: { x: referrer.x, y: referrer.y },
            target: { x: node.x, y: node.y }
          });
        }
      }
    });
    
    console.log(`Created radial layout with ${radialNodes.length} nodes and ${radialLinks.length} links`);
    
    // No CSS animations - using SVG native animations instead
    
    // Create links with curved paths
    const link = g.append("g")
      .attr("class", "links")
      .selectAll("path")
      .data(radialLinks)
      .enter()
      .append("path")
      .attr("d", d => {
        // Create a curved path between nodes
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const dr = Math.sqrt(dx * dx + dy * dy) * 1.5; // Curve factor
        return `M${d.source.x},${d.source.y}A${dr},${dr} 0 0,1 ${d.target.x},${d.target.y}`;
      })
      .attr("fill", "none")
      .attr("stroke", "#33ff33")
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.6);
    
    // Create nodes
    const node = g.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(radialNodes)
      .enter()
      .append("g")
      .attr("class", d => `node node-rank-${parseInt(d.rank || '0')}`)
      .attr("transform", d => `translate(${d.x},${d.y})`)
      .style("transform-origin", d => `${centerX - d.x}px ${centerY - d.y}px`)
      .on("click", (event, d) => {
        // Prevent event propagation
        event.stopPropagation();
        event.preventDefault();
        
        // Just update the state without any visual changes here
        // The visual changes will be handled separately
        const newHighlightedId = d.id === highlightedUserId ? null : d.id;
        setHighlightedUserId(newHighlightedId);
        
        // Directly update the appearance of all nodes
        d3.selectAll(".node circle")
          .attr("stroke", function() {
            const nodeData = d3.select(this.parentNode).datum();
            return nodeData.id === newHighlightedId ? "#ffff33" :
                  (nodeData.id === linkedProfile?.playerId ? "#33ffaa" :
                  getRankColor(nodeData.rank));
          })
          .attr("stroke-width", function() {
            const nodeData = d3.select(this.parentNode).datum();
            return (nodeData.id === newHighlightedId || nodeData.id === linkedProfile?.playerId) ?
                  calculateStrokeWidth(nodeData.qubits, true) :
                  calculateStrokeWidth(nodeData.qubits, false);
          });
      })
      .on("mouseover", (event, d) => {
        setTooltipInfo({
          x: event.pageX,
          y: event.pageY,
          username: d.username,
          qubits: d.qubits,
          rank: d.rank || '0',
          referrals: d.children ? d.children.length : 0,
          depth: 0, // Not relevant in pyramid view
          isCurrentUser: d.id === linkedProfile?.playerId
        });
      })
      .on("mouseout", () => {
        setTooltipInfo(null);
      });
    
    // Get rank color based on rank
    const getRankColor = (rank) => {
      const rankColors = {
        '0': "#A9A9A9", // Light Gray
        '1': "#00BFFF", // Deep Sky Blue
        '2': "#9370DB", // Medium Purple
        '3': "#FFD700", // Gold
        '4': "#008080", // Teal
        '5': "#8A2BE2", // Blue Violet
        '6': "#FD6907"  // Dark Orange
      };
      return rankColors[rank] || "#33ff33";
    };
    
    // Get rank name
    const getRankName = (rank) => {
      const rankNames = {
        '0': "Signal Seeker",
        '1': "Void Aligned",
        '2': "Sentient",
        '3': "Frequency Specialist",
        '4': "Echo Bound",
        '5': "Resonance Manipulator",
        '6': "VOID"
      };
      return rankNames[rank] || "Unknown";
    };

    // Large flower circle — no pointer events, purely visual
    node.append("circle")
      .attr("class", "flower-circle")
      .attr("r", nodeR)
      .attr("cx", 0)
      .attr("cy", 0)
      .style("pointer-events", "none")
      .attr("fill", d => {
        const baseColor = getRankColor(d.rank);
        const r = parseInt(baseColor.slice(1, 3), 16);
        const gVal = parseInt(baseColor.slice(3, 5), 16);
        const b = parseInt(baseColor.slice(5, 7), 16);
        const opacity = 0.015;
        return d.id === linkedProfile?.playerId ?
          `rgba(0, 255, 170, ${opacity})` :
          `rgba(${r}, ${gVal}, ${b}, ${opacity})`;
      })
      .attr("stroke", d => d.id === highlightedUserId ? "#ffff33" :
                           (d.id === linkedProfile?.playerId ? "#33ffaa" :
                           getRankColor(d.rank)))
      .attr("stroke-width", d => (d.id === highlightedUserId || d.id === linkedProfile?.playerId) ? 1 : 0.4)
      .attr("stroke-opacity", 0.15)
      .attr("filter", d => `url(#glow-${Math.ceil(calculateGlowIntensity(d.qubits))})`);

    // Invisible hit target — sized between label hub and full circle
    node.append("circle")
      .attr("r", nodeR * 0.4)
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("fill", "transparent")
      .attr("stroke", "none");

    // Small label hub at the center of each node circle
    node.append("circle")
      .attr("r", 28)
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("fill", "rgba(0, 5, 0, 0.85)")
      .attr("stroke", d => d.id === highlightedUserId ? "#ffff33" :
                           (d.id === linkedProfile?.playerId ? "#33ffaa" :
                           getRankColor(d.rank)))
      .attr("stroke-width", 1)
      .attr("stroke-opacity", 0.6);

    // Rank indicator text
    node.append("text")
      .attr("dy", -6)
      .attr("text-anchor", "middle")
      .style("pointer-events", "none")
      .attr("fill", d => d.id === highlightedUserId ? "#ffff33" :
                         (d.id === linkedProfile?.playerId ? "#33ffaa" :
                         getRankColor(d.rank)))
      .attr("font-size", 10)
      .attr("font-family", "IBM BIOS, monospace")
      .attr("font-weight", "bold")
      .text(d => `R${d.rank}`);

    // Username label
    node.append("text")
      .attr("dy", 10)
      .attr("text-anchor", "middle")
      .style("pointer-events", "none")
      .attr("fill", d => d.id === highlightedUserId ? "#ffff33" :
                         (d.id === linkedProfile?.playerId ? "#33ffaa" :
                         getRankColor(d.rank)))
      .attr("font-size", 9)
      .attr("font-family", "IBM BIOS, monospace")
      .text(d => d.username.substring(0, 7));
    
    // Calculate bounds to determine zoom — account for full node circle radius
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    radialNodes.forEach(d => {
      minX = Math.min(minX, d.x - nodeR);
      maxX = Math.max(maxX, d.x + nodeR);
      minY = Math.min(minY, d.y - nodeR);
      maxY = Math.max(maxY, d.y + nodeR);
    });

    // Add padding
    const padding = nodeR * 0.5;
    minX -= padding;
    maxX += padding;
    minY -= padding;
    maxY += padding;
    
    // Calculate zoom to fit all nodes
    const dx = maxX - minX;
    const dy = maxY - minY;
    const scale = 0.9 / Math.max(dx / width, dy / height);
    const translate = [
      (width / 2) - scale * (minX + maxX) / 2,
      (height / 2) - scale * (minY + maxY) / 2
    ];
    
    // Apply zoom transform to fit all nodes
    const initialTransform = d3.zoomIdentity
      .translate(translate[0], translate[1])
      .scale(scale);
    
    svg.call(zoom.transform, initialTransform);
    
    // Background — minimal, nodes form the flower
    const usedRings = radialNodes.length > 0 ? Math.max(...radialNodes.map(n => n.radius / nodeR)) + 2 : 5;
    const maxRadius = usedRings * nodeR;

    // Ambient particles
    const particles = g.append("g")
      .attr("class", "ambient-particles");

    for (let i = 0; i < 20; i++) {
      const pAngle = Math.random() * Math.PI * 2;
      const distance = Math.random() * maxRadius * 0.7;
      const px = centerX + Math.cos(pAngle) * distance;
      const py = centerY + Math.sin(pAngle) * distance;
      const size = Math.random() * 1.5 + 0.5;

      const particle = particles.append("circle")
        .attr("cx", px)
        .attr("cy", py)
        .attr("r", size)
        .attr("fill", "#33ff33")
        .attr("opacity", Math.random() * 0.12 + 0.03);

      particle.append("animate")
        .attr("attributeName", "opacity")
        .attr("values", `${Math.random() * 0.04 + 0.02};${Math.random() * 0.12 + 0.06};${Math.random() * 0.04 + 0.02}`)
        .attr("dur", `${Math.random() * 5 + 3}s`)
        .attr("repeatCount", "indefinite");
    }

    // Data readouts
    const dataReadouts = g.append("g")
      .attr("class", "data-readouts");

    const readoutTexts = [
      "SYS:ONLINE",
      "SCAN:ACTIVE",
      "VOID:STABLE",
      `NODES:${radialNodes.length}`,
      `LINKS:${radialLinks.length}`,
      "GEOMETRY:SACRED"
    ];

    readoutTexts.forEach((text, i) => {
      const rx = (i < 3) ? centerX - maxRadius * 0.8 : centerX + maxRadius * 0.8;
      const ry = centerY - maxRadius * 0.8 + (i % 3) * 20;

      dataReadouts.append("text")
        .attr("x", rx)
        .attr("y", ry)
        .attr("text-anchor", (i < 3) ? "start" : "end")
        .attr("font-family", "IBM BIOS, monospace")
        .attr("font-size", 10)
        .attr("fill", "#33ff33")
        .attr("opacity", 0.5)
        .text(text);
    });
    
    // VOID node — the singularity at the heart of the flower
    const voidNode = g.append("g")
      .attr("class", "void-node")
      .style("pointer-events", "none")
      .attr("transform", `translate(${centerX}, ${centerY})`);

    // Outer pulse ring
    const voidPulse = voidNode.append("circle")
      .attr("r", 45)
      .attr("fill", "none")
      .attr("stroke", "#FF0000")
      .attr("stroke-width", 0.5)
      .attr("stroke-opacity", 0.3);

    voidPulse.append("animate")
      .attr("attributeName", "r")
      .attr("values", "40;65;40")
      .attr("dur", "4s")
      .attr("repeatCount", "indefinite");

    voidPulse.append("animate")
      .attr("attributeName", "stroke-opacity")
      .attr("values", "0.3;0.05;0.3")
      .attr("dur", "4s")
      .attr("repeatCount", "indefinite");

    // Second pulse ring (offset timing)
    const voidPulse2 = voidNode.append("circle")
      .attr("r", 30)
      .attr("fill", "none")
      .attr("stroke", "#FF0000")
      .attr("stroke-width", 0.3)
      .attr("stroke-opacity", 0.2);

    voidPulse2.append("animate")
      .attr("attributeName", "r")
      .attr("values", "55;80;55")
      .attr("dur", "4s")
      .attr("begin", "2s")
      .attr("repeatCount", "indefinite");

    voidPulse2.append("animate")
      .attr("attributeName", "stroke-opacity")
      .attr("values", "0.2;0.0;0.2")
      .attr("dur", "4s")
      .attr("begin", "2s")
      .attr("repeatCount", "indefinite");

    // Core glow
    voidNode.append("circle")
      .attr("r", 30)
      .attr("fill", "rgba(80, 0, 0, 0.6)")
      .attr("stroke", "none")
      .attr("filter", "url(#glow-3)");

    // Inner ring
    voidNode.append("circle")
      .attr("r", 25)
      .attr("fill", "rgba(0, 0, 0, 0.9)")
      .attr("stroke", "#FF0000")
      .attr("stroke-width", 1.5)
      .attr("stroke-opacity", 0.7);

    // Rotating inner detail
    const voidSpin = voidNode.append("g");

    voidSpin.append("animateTransform")
      .attr("attributeName", "transform")
      .attr("type", "rotate")
      .attr("from", "0")
      .attr("to", "360")
      .attr("dur", "20s")
      .attr("repeatCount", "indefinite");

    // Small diamond markers rotating inside
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      voidSpin.append("rect")
        .attr("x", Math.cos(a) * 18 - 2)
        .attr("y", Math.sin(a) * 18 - 2)
        .attr("width", 4)
        .attr("height", 4)
        .attr("transform", `rotate(45 ${Math.cos(a) * 18} ${Math.sin(a) * 18})`)
        .attr("fill", "#FF0000")
        .attr("opacity", 0.5);
    }

    // VOID text
    voidNode.append("text")
      .attr("dy", 1)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#FF0000")
      .attr("font-size", 12)
      .attr("font-family", "IBM BIOS, monospace")
      .attr("font-weight", "bold")
      .attr("letter-spacing", "3px")
      .text("VOID");

    // Subtle text glow
    voidNode.append("text")
      .attr("dy", 1)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#FF0000")
      .attr("font-size", 12)
      .attr("font-family", "IBM BIOS, monospace")
      .attr("font-weight", "bold")
      .attr("letter-spacing", "3px")
      .attr("filter", "url(#glow-2)")
      .attr("opacity", 0.4)
      .text("VOID");
    
    // Create a modern, minimalist scanner effect
    const scannerGroup = g.append("g")
      .attr("class", "scanner-effect")
      .attr("transform", `translate(${centerX}, ${centerY})`);
    
    // Create a linear gradient for the scanner line
    const scannerGradient = defs.append("linearGradient")
      .attr("id", "scannerGradient")
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "100%")
      .attr("y2", "0%");
    
    scannerGradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", "0.3"); // Increased opacity
      
    scannerGradient.append("stop")
      .attr("offset", "50%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", "0.9"); // Increased opacity
      
    scannerGradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#ffffff")
      .attr("stop-opacity", "0.3"); // Increased opacity
    
    // Create a more visible scanner line
    const scannerLine = scannerGroup.append("line")
      .attr("x1", -maxRadius)
      .attr("y1", 0)
      .attr("x2", maxRadius)
      .attr("y2", 0)
      .attr("stroke", "url(#scannerGradient)")
      .attr("stroke-width", 2) // Increased width
      .attr("stroke-dasharray", "3,2") // Adjusted dash pattern
      .attr("opacity", 0.9) // Increased opacity
      .attr("filter", "url(#glow-1)"); // Added subtle glow
    
    // Add a more noticeable pulse effect to the scanner line
    scannerLine.append("animate")
      .attr("attributeName", "stroke-width")
      .attr("values", "1.5;3;1.5") // Increased width range
      .attr("dur", "3s") // Faster animation
      .attr("repeatCount", "indefinite");
    
    // Add rotation animation to the scanner group - slightly faster
    scannerGroup.append("animateTransform")
      .attr("attributeName", "transform")
      .attr("type", "rotate")
      .attr("from", "0")
      .attr("to", "360")
      .attr("dur", "15s") // Slightly faster rotation
      .attr("repeatCount", "indefinite")
      .attr("additive", "sum");
    
    // Add more prominent markers along the scanner line
    for (let i = 1; i <= 5; i++) {
      const position = maxRadius * (i / 6);
      
      // Add marker on positive side
      scannerGroup.append("circle")
        .attr("r", i % 2 === 0 ? 2 : 3) // Alternating sizes, larger overall
        .attr("cx", position)
        .attr("cy", 0)
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9) // Higher opacity
        .attr("filter", "url(#glow-1)") // Add glow
        .append("animate")
        .attr("attributeName", "opacity")
        .attr("values", "0.6;1;0.6") // Higher opacity range
        .attr("dur", `${1.5 + i * 0.5}s`) // Faster, different duration for each marker
        .attr("repeatCount", "indefinite");
      
      // Add marker on negative side
      scannerGroup.append("circle")
        .attr("r", i % 2 === 0 ? 2 : 3) // Alternating sizes, larger overall
        .attr("cx", -position)
        .attr("cy", 0)
        .attr("fill", "#ffffff")
        .attr("opacity", 0.9) // Higher opacity
        .attr("filter", "url(#glow-1)") // Add glow
        .append("animate")
        .attr("attributeName", "opacity")
        .attr("values", "0.6;1;0.6") // Higher opacity range
        .attr("dur", `${1.5 + i * 0.5}s`) // Faster, different duration for each marker
        .attr("repeatCount", "indefinite");
    }
    
    // Add a central marker
    scannerGroup.append("circle")
      .attr("r", 4) // Larger central marker
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("fill", "#ffffff")
      .attr("opacity", 0.9)
      .attr("filter", "url(#glow-2)")
      .append("animate")
      .attr("attributeName", "r")
      .attr("values", "3;5;3")
      .attr("dur", "2s")
      .attr("repeatCount", "indefinite");
    
    // Seed of Life circles already rendered in background group
    
  }, [networkData]); // Remove highlightedUserId dependency to prevent re-rendering
  
  // Effect to update node highlighting when highlightedUserId changes
  // This is separate from the main visualization rendering
  useEffect(() => {
    if (!svgRef.current || !networkData) return;
    
    // Update node highlighting based on highlightedUserId
    d3.selectAll(".node circle")
      .attr("stroke", function() {
        const nodeData = d3.select(this.parentNode).datum();
        return nodeData.id === highlightedUserId ? "#ffff33" :
              (nodeData.id === linkedProfile?.playerId ? "#33ffaa" :
              getRankColor(nodeData.rank));
      })
      .attr("stroke-width", function() {
        const nodeData = d3.select(this.parentNode).datum();
        return (nodeData.id === highlightedUserId || nodeData.id === linkedProfile?.playerId) ?
              calculateStrokeWidth(nodeData.qubits, true) :
              calculateStrokeWidth(nodeData.qubits, false);
      });
      
  }, [highlightedUserId, linkedProfile]);
  
  // Count total nodes in the network
  const countTotalNodes = (node) => {
    if (!node) return 0;
    
    let count = 1; // Count this node
    
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        count += countTotalNodes(child);
      });
    }
    
    return count;
  };

  if (loading) {
    return (
      <div className="referral-network-module">
        <div className="module-header">
          <button onClick={onBack} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            BACK
          </button>
          <h1>ORBITAL MAP</h1>
        </div>
        <div className="loading-container">
          <div className="loading-text">MAPPING QUANTUM NETWORK...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-network-module">
        <div className="module-header">
          <button onClick={onBack} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            BACK
          </button>
          <h1>ORBITAL MAP</h1>
        </div>
        <div className="error-container">
          <div className="error-text">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="referral-network-module">
      <div className="module-header">
        <button onClick={onBack} className="back-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          BACK
        </button>
        <h1>ORBITAL MAP</h1>
      </div>

      <div className="network-container">
        <svg
          ref={svgRef}
          width="100%"
          height="calc(100vh - 180px)"
          className="network-svg"
          viewBox={`0 0 ${window.innerWidth * 0.9} ${window.innerHeight - 180}`}
          preserveAspectRatio="xMidYMid meet"
        ></svg>
        
        {tooltipInfo && (
          <div
            className="node-tooltip"
            style={{
              left: `${tooltipInfo.x + 5}px`, // Closer to mouse (5px offset instead of 10px)
              top: `${tooltipInfo.y + 5}px`,  // Closer to mouse (5px offset instead of 10px)
              transition: 'opacity 0.1s ease', // Snappier transition
              opacity: 1,
              pointerEvents: 'none' // Prevent tooltip from interfering with mouse events
            }}
            ref={tooltipRef}
          >
            <div className="tooltip-header">
              {tooltipInfo.username}
              {tooltipInfo.isCurrentUser && <span className="current-user-badge">YOU</span>}
            </div>
            <div className="tooltip-content">
              <div className="tooltip-stat">
                <span className="tooltip-label">Rank:</span>
                <span className="tooltip-value" style={{ color: getRankColor(tooltipInfo.rank) }}>
                  {tooltipInfo.rank} - {getRankName(tooltipInfo.rank)}
                </span>
              </div>
              <div className="tooltip-stat">
                <span className="tooltip-label">Qubits:</span>
                <span className="tooltip-value">{tooltipInfo.qubits.toLocaleString()}</span>
              </div>
              <div className="tooltip-stat">
                <span className="tooltip-label">Referrals:</span>
                <span className="tooltip-value">{tooltipInfo.referrals}</span>
              </div>
            </div>
            <div className="tooltip-footer">Click to focus</div>
          </div>
        )}
      </div>
      
      <div className="network-stats">
        <div className="stat-item">
          <span className="stat-label">Total Network Size:</span>
          <span className="stat-value">{allNodes ? allNodes.length : 0} sentients</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Layout:</span>
          <span className="stat-value">Seed of Life · R6 center → R0 outer</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Instructions:</span>
          <span className="stat-value">Drag to move, scroll to zoom</span>
        </div>
      </div>
    </div>
  );
};

export default ReferralNetworkModule;